import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, Bell, Plus } from "lucide-react";
import { cfBus, openAddProperty, useChainStore } from "../../data/chainStore";
import { ChainFlowLogo } from "../brand/ChainFlowLogo";

/* ═══════════════════════════════════════════════════════════════════
   MobileTopBar — premium sticky header for mobile workspace pages
   --------------------------------------------------------------------
   - Visible only at < md
   - Hidden on landing, auth and Deal Room (which has its own sticky header)
   - Left: hamburger → opens sidebar drawer via cf:sidebar:toggle
   - Center: route-derived page title (compact, premium)
   - Right: contextual primary action (Bell with badge OR Plus to create deal)
   ─────────────────────────────────────────────────────────────────── */

const PUBLIC_ROUTES = [
  "/", "/login", "/signup", "/register", "/forgot-password", "/reset-password",
  "/verify-email", "/accept-invite", "/join-deal", "/pricing", "/pipeline-audit",
  "/about", "/legal", "/legal/terms", "/legal/privacy", "/legal/cookies",
  "/solicitor-portal",
];

const isPublic = (pathname) =>
  PUBLIC_ROUTES.some((p) => p === pathname || (p !== "/" && pathname.startsWith(p)));

// Deal Room has its own MobileStickyHeader — don't double up.
// Sales Progression also has a cinematic dark operational shell with
// its own hamburger / logo / bell (iter-179) so suppress here too.
const hasOwnHeader = (pathname) =>
  /^\/deal-rooms\/[^/]+/.test(pathname)
  || pathname === "/today"
  || pathname === "/sales-progression"
  || pathname.startsWith("/sales-progression/");

const TITLE_MAP = [
  { match: (p) => p === "/dashboard" || p === "/today",  title: "Flow Centre" },
  { match: (p) => p.startsWith("/deals"),            title: "Transactions" },
  { match: (p) => p.startsWith("/deal-rooms"),       title: "Deal Rooms" },
  { match: (p) => p.startsWith("/offer-intelligence"), title: "Offer Intelligence" },
  { match: (p) => p.startsWith("/documents"),        title: "Documents" },
  { match: (p) => p.startsWith("/properties"),       title: "Sellers" },
  { match: (p) => p.startsWith("/contacts"),         title: "Contacts" },
  { match: (p) => p.startsWith("/viewings"),         title: "Viewings" },
  { match: (p) => p.startsWith("/team"),             title: "Team" },
  // iter-alignment · P56 — `/people` legacy route redirects to
  // `/contacts` in App.js; keep the title mapping in case any
  // deep-links from stale bookmarks still route through this bar
  // before the redirect fires.
  { match: (p) => p.startsWith("/people"),           title: "Contacts" },
  { match: (p) => p.startsWith("/insights"),         title: "Analytics" },
  { match: (p) => p.startsWith("/director"),         title: "Director Centre" },
  { match: (p) => p.startsWith("/revenue"),          title: "Revenue Control" },
  { match: (p) => p.startsWith("/office-performance"), title: "Office Performance" },
  { match: (p) => p.startsWith("/notifications"),    title: "Notifications" },
  { match: (p) => p.startsWith("/alerts"),           title: "Notifications" },
  { match: (p) => p.startsWith("/settings"),         title: "Settings" },
  { match: (p) => p.startsWith("/flow"),             title: "Flow" },
  { match: (p) => p.startsWith("/seller-portal"),    title: "Seller Portal" },
  { match: (p) => p.startsWith("/buyer-portal"),     title: "Buyer Portal" },
  { match: (p) => p.startsWith("/seller-launch-hub"), title: "Launch Hub" },
  { match: (p) => p.startsWith("/marketplace"),      title: "Marketplace" },
  { match: (p) => p.startsWith("/chain-chat"),       title: "Chain Chat" },
  { match: (p) => p.startsWith("/command"),          title: "Flow Centre" },
];

const titleFor = (pathname) => TITLE_MAP.find((m) => m.match(pathname))?.title || "ChainFlow OS";

// Pages where Plus = create deal makes sense
const SHOW_CREATE = (p) =>
  p === "/dashboard" || p.startsWith("/deals") || p.startsWith("/properties");

export const MobileTopBar = () => {
  const location = useLocation();
  const nav = useNavigate();
  const { notifications } = useChainStore();
  const unread = (notifications || []).filter((n) => !n.read).length;

  if (isPublic(location.pathname)) return null;
  if (hasOwnHeader(location.pathname)) return null;

  const title = titleFor(location.pathname);
  const showCreate = SHOW_CREATE(location.pathname);

  const handleHamburger = () => {
    window.dispatchEvent(new CustomEvent("cf:sidebar:toggle"));
  };

  const handleBell = () => {
    if (unread > 0) {
      cfBus.emit("cf:open-notifications");
    } else {
      nav("/notifications");
    }
  };

  return (
    <header
      className="md:hidden sticky top-0 z-40 flex items-center gap-2 px-3"
      style={{
        background: "rgba(255,255,255,0.86)",
        backdropFilter: "blur(14px) saturate(160%)",
        WebkitBackdropFilter: "blur(14px) saturate(160%)",
        borderBottom: "1px solid rgba(15,23,42,0.06)",
        boxShadow: "0 1px 0 rgba(255,255,255,0.55) inset",
        paddingTop: "env(safe-area-inset-top, 0px)",
        height: "calc(56px + env(safe-area-inset-top, 0px))",
      }}
      data-testid="mobile-topbar"
    >
      {/* Hamburger */}
      <button
        type="button"
        onClick={handleHamburger}
        aria-label="Open navigation"
        className="w-10 h-10 rounded-xl flex items-center justify-center text-slate-700 active:scale-95 transition-transform"
        style={{
          background: "rgba(15,23,42,0.04)",
          border: "1px solid rgba(15,23,42,0.06)",
        }}
        data-testid="mobile-topbar-hamburger"
      >
        <Menu size={18} strokeWidth={2.2} />
      </button>

      {/* Brand + title — tap to go home */}
      <button
        type="button"
        onClick={() => nav("/dashboard")}
        className="flex items-center gap-2.5 min-w-0 flex-1 text-left"
        data-testid="mobile-topbar-title"
      >
        <ChainFlowLogo variant="icon" size={28} testid="mobile-topbar-brand-icon" />
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-indigo-700 leading-none">
            ChainFlow
          </p>
          <p
            className="font-display text-[14.5px] font-bold text-slate-900 truncate leading-tight mt-0.5"
            data-testid="mobile-topbar-page-title"
          >
            {title}
          </p>
        </div>
      </button>

      {/* Right cluster — bell + optional create */}
      <div className="flex items-center gap-1.5 shrink-0">
        <button
          type="button"
          onClick={handleBell}
          aria-label="Notifications"
          className="relative w-10 h-10 rounded-xl flex items-center justify-center text-slate-700 active:scale-95 transition-transform"
          style={{
            background: "rgba(15,23,42,0.04)",
            border: "1px solid rgba(15,23,42,0.06)",
          }}
          data-testid="mobile-topbar-bell"
        >
          <Bell size={16} strokeWidth={2.1} />
          {unread > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[9.5px] font-bold tabular-nums rounded-full inline-flex items-center justify-center text-white"
              style={{
                background: "linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)",
                boxShadow: "0 0 0 2px #fff, 0 4px 10px -2px rgba(124,58,237,0.55)",
              }}
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>

        {showCreate && (
          <button
            type="button"
            onClick={() => openAddProperty({})}
            aria-label="Add property"
            className="w-10 h-10 rounded-xl flex items-center justify-center text-white active:scale-95 transition-transform"
            style={{
              background: "linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)",
              boxShadow:
                "0 10px 22px -8px rgba(124,58,237,0.55), inset 0 1px 0 rgba(255,255,255,0.18)",
            }}
            data-testid="mobile-topbar-create"
          >
            <Plus size={17} strokeWidth={2.4} />
          </button>
        )}
      </div>
    </header>
  );
};

export default MobileTopBar;
